import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  BillingRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { HubSpotContact } from '../../../api/billing-types';
import { BillingKeys, deleteHubSpotContact } from '../../../api/billing-api';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeletePaymentContactDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedContact: HubSpotContact;
  changeLogRoles: string[];
  hubSpotCompanyId: number;
}

export default function DeletePaymentContactDialog({
  open,
  handleClose,
  selectedContact,
  changeLogRoles,
  hubSpotCompanyId,
}: DeletePaymentContactDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteHubSpotContact(selectedContact, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Billing contact deleted successfully!',
      });

      queryClient.setQueryData<HubSpotContact[] | undefined>(
        [BillingKeys.HubSpotContacts, hubSpotCompanyId],
        (oldData) => {
          if (!oldData) return;
          return oldData.filter((contact) => contact.id !== selectedContact.id);
        }
      );

      setChangeLogMessage('');
      setOpenChangeLogDialog(false);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting this billing contact. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.billing.billing_contacts.delete_billing_contact, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.billingContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, BillingRoles.canRemoveContacts.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Billing Contact"
      dialogContent={
        <Box>
          <Typography>
            Delete <strong>{selectedContact.properties.email}</strong> as a
            billing contact from your account?
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete Contact',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
